import { ZAttribute } from "src/types/ZAttribute";
import { ZRestriction } from "src/types/ZRestriction";
import { ZAttrEditInfo, zAttrEditInfo } from "src/common/attrEdit";
import { ZAttrViewInfo, zAttrViewInfo } from "src/common/attrView";
import { ZTranslationMap } from "src/types/ZTranslation";
import { ParamsSerializerOptions } from "axios";
import { stdParamsSerializer } from "src/common/rest";
import { ZUid } from "src/types/zUid";
import {
  zCopyByValueRule,
  ZCopyByValueRule,
} from "src/common/attrEdit/ZCopyByValueRule";
import {
  AttrRestrictions,
  cvtRestrictionsDictToFlat,
  cvtRestrictionsFlatToDict,
} from "./forms/RestrictionsTable/restrictionUtils";
import { EdObjStates } from "./EdObject";

/**
 * Тип данных для описания атрибута ZAttribute
 * Но для формы редактирования он не подходит.
 * Поэтому форма использует тип EdAttribute
 */
export type EdAttribute = Omit<
  ZAttribute,
  "values" | "viewStyles" | "viewType"
> & {
  useRestriction: boolean; // если false, то attributeValueRestriction=null
  masterAttrId?: ZUid; // id атрибута, от которого зависит этот атрибут
  restrictions?: AttrRestrictions;
  editorInfo?: ZAttrEditInfo; // формируется из viewStyles
  viewInfo?: ZAttrViewInfo; // формируется из viewType
  rolesMap: EdObjStates;
  translations?: ZTranslationMap;
  copyStatesForMnemonicGroup?: ZUid[];
};

// чтобы использовать вместо строк в <Form.Item name="...">
export const edAttrField = {
  fieldKey: "fieldKey",
  masterAttrId: "masterAttrId",
  name: "name",
  referenceId: "referenceId",
  restrictions: "restrictions",
  useRestriction: "useRestriction",
  valueType: "valueType",
  editorInfo: "editorInfo",
  viewInfo: "viewInfo",
  rolesMap: "rolesMap",
  roleIds: "roleIds",
  translations: "translations",
  iterator: "iterator",
  clearByCopy: "clearByCopy",
  pathValue: "pathValue",
  articleNumberInfo: "articleNumberInfo",
  filter: "filter",
  backlinkAttributeId: "backlinkAttributeId",
  childDictionaryAttributeId: "childDictionaryAttributeId",
  childDictionaryValueId: "childDictionaryValueId",
  copyStatesForMnemonicGroup: "copyStatesForMnemonicGroup",
  massReplacement: "massReplacement",
  autofillByCopy: "autofillByCopy",
  uniqueValue: "uniqueValue",
} as const satisfies Partial<Record<keyof EdAttribute, string>>;

export const attr2edit = (
  attr: ZAttribute,
  rolesMap: EdObjStates,
  restrictions?: ZRestriction | null,
  translations?: ZTranslationMap,
): EdAttribute => {
  const {
    viewStyles,
    viewType,
    linkAttributeId,
    parentAttributeId,
    withRestrictions,
    filter,
    ...other
  } = attr;
  const result: EdAttribute = {
    ...other,
    useRestriction: !!restrictions,
    masterAttrId: restrictions?.restrictionAttributeId,
    restrictions: cvtRestrictionsFlatToDict(restrictions?.values ?? []),
    rolesMap,
    translations,
    filter,
  };
  const copyByValueRule: ZCopyByValueRule = {
    linkAttributeId,
    parentAttributeId,
    withRestrictions,
  };
  // Распарсить информацию о компоненте редактирования. Она поступает в текстовом виде
  try {
    if (viewStyles) {
      result.editorInfo = zAttrEditInfo.parse(JSON.parse(viewStyles));
      const { itemProps } = result.editorInfo;
      result.editorInfo.itemProps = {
        ...itemProps,
        copyByValueRule: {
          ...itemProps?.copyByValueRule,
          ...zCopyByValueRule.parse(copyByValueRule),
        },
      };
    }
    if (viewType) {
      result.viewInfo = zAttrViewInfo.parse(JSON.parse(viewType));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  if (result.articleNumberInfo) {
    result.articleNumberInfo = {
      ...result.articleNumberInfo,
      alphanumericCounter:
        result.articleNumberInfo.alphanumericCounter ?? false,
    };
  }
  return result;
};

export type AttrSaveParams = {
  groupIdsToCopy?: ZUid[] | null;
};

export type AttrSaveConfig = {
  params?: AttrSaveParams;
  paramsSerializer: ParamsSerializerOptions;
};

type ResEdit2Attr = {
  attr: Partial<ZAttribute>;
  restrictions: ZRestriction | null;
  rolesMap: EdObjStates;
  translations?: ZTranslationMap;
  config?: AttrSaveConfig;
  copyStatesForMnemonicGroup?: ZUid[];
};
export const edit2attr = (edAttr: EdAttribute): ResEdit2Attr => {
  const {
    useRestriction,
    masterAttrId,
    restrictions,
    editorInfo,
    viewInfo,
    rolesMap: formRolesMap,
    translations,
    filter,
    backlinkAttributeId,
    childDictionaryAttributeId,
    childDictionaryValueId,
    copyStatesForMnemonicGroup,
    ...other
  } = edAttr;

  const zres: ZRestriction | null =
    useRestriction && masterAttrId && restrictions
      ? {
          restrictionAttributeId: masterAttrId,
          values: cvtRestrictionsDictToFlat(restrictions),
        }
      : null;
  const filterRes = filter || null;
  // На форме есть поле editorInfo.component.editor. И если оно пусто, то нужно вообще убрать component. Иначе не проходит zod-валидация
  const validEditorInfo = editorInfo ? { ...editorInfo } : undefined;
  if (validEditorInfo && !validEditorInfo.component?.editor) {
    delete validEditorInfo.component;
  }

  const { groupIdsToCopy, ...copyByValueRule } = zCopyByValueRule.parse({
    ...validEditorInfo?.itemProps?.copyByValueRule,
  });

  if (validEditorInfo?.itemProps?.copyByValueRule) {
    validEditorInfo.itemProps.copyByValueRule = { groupIdsToCopy };
  } else {
    other.parentAttributeId = null;
    other.linkAttributeId = null;
    other.withRestrictions = null;
  }

  const isParams = !!groupIdsToCopy;
  const config: AttrSaveConfig | undefined = isParams
    ? {
        params: {
          groupIdsToCopy,
        },
        paramsSerializer: stdParamsSerializer,
      }
    : undefined;

  const validViewInfo = viewInfo ? { ...viewInfo } : undefined;
  if (validViewInfo) {
    // На форме есть поле viewInfo.component.view. И если оно пусто, то нужно вообще убрать component. Иначе не проходит zod-валидация

    if (!validViewInfo.component?.view) {
      delete validViewInfo.component;
    }

    // Проверяем viewInfo.styles.[zAttrViewStyles] и если там нет значений, то удаляем их.
    validViewInfo.styles = Object.fromEntries(
      Object.entries(validViewInfo.styles ?? {}).filter(
        ([, value]) => typeof value !== "undefined" && value !== null,
      ),
    );

    if (Object.keys(validViewInfo.styles).length === 0) {
      delete validViewInfo.styles;
    }
  }

  if (other.articleNumberInfo) {
    other.articleNumberInfo = {
      ...other.articleNumberInfo,
      alphanumericCounter: other.articleNumberInfo.alphanumericCounter ?? false,
    };
  }
  return {
    attr: {
      ...other,
      viewStyles: JSON.stringify(
        zAttrEditInfo.parse({ ...validEditorInfo, ver: 1 }),
      ),
      viewType: JSON.stringify(
        zAttrViewInfo.parse({ ...validViewInfo, ver: 1 }),
      ),
      ...copyByValueRule,
      filter: filterRes,
      backlinkAttributeId: backlinkAttributeId || null,
      childDictionaryAttributeId: childDictionaryAttributeId || null,
      childDictionaryValueId:
        childDictionaryAttributeId && childDictionaryValueId
          ? childDictionaryValueId
          : null,
    },
    restrictions: zres,
    // Ant Form omits this field when the object has no state permission rows.
    // Keep the converter result stable because the save flow calls Object.entries.
    rolesMap: formRolesMap ?? {},
    translations,
    config,
    copyStatesForMnemonicGroup,
  };
};
